import { Marker, Polyline } from '@react-google-maps/api';
import { useContext, useState } from 'react';

import { cssVariables } from '../../../styles/cssVariables';
import { mapSize, point } from '../../../utils/mapUtils';
import { svgCirclePath } from '../../../utils/svgUtils';
import { GoogleMapClustererContext } from './googleMapClustering';
import { useGoogleMapTooltip } from './googleMapTooltips';

// ICONS, SYMBOLS
// https://developers.google.com/maps/documentation/javascript/reference/marker#Icon
// https://developers.google.com/maps/documentation/javascript/reference/marker#Symbol

const PLANE_PATH =
  'M552 264C582.9 264 608 289.1 608 320C608 350.9 582.9 376 552 376L424.7 376L265.5 549.6C259.4 556.2 250.9 560 241.9 560L198.2 560C187.3 560 179.6 549.3 183 538.9L237.3 376L137.6 376L84.8 442C81.8 445.8 77.2 448 72.3 448L52.5 448C42.1 448 34.5 438.2 37 428.1L64 320L37 211.9C34.4 201.8 42.1 192 52.5 192L72.3 192C77.2 192 81.8 194.2 84.8 198L137.6 264L237.3 264L183 101.1C179.6 90.7 187.3 80 198.2 80L241.9 80C250.9 80 259.4 83.8 265.5 90.4L424.7 264L552 264z';

function circleIcon(color) {
  return {
    scale: 1,
    path: svgCirclePath(9),
    strokeWeight: 1,
    strokeColor: cssVariables.colorWhite,
    fillOpacity: 1,
    fillColor: color,
    labelOrigin: point(9, 9),
    anchor: point(10, 10),
    size: mapSize(20, 20),
  };
}

function dashedLineSymbol(color) {
  return {
    path: 'M 0,-0.5 0,0.5',
    strokeOpacity: 0.5,
    strokeWeight: 3,
    scale: 3,
    strokeColor: color || cssVariables.colorRed,
  };
}

export function imgIcon(url, size, anchor) {
  return {
    url,
    size,
    anchor: anchor || point(size?.width / 2 || 0, size?.height / 2 || 0),
  };
}

// LABELS
// https://developers.google.com/maps/documentation/javascript/reference/marker#MarkerLabel

function shortLabel(label) {
  return {
    text: label,
    color: cssVariables.colorWhite,
    fontFamily: cssVariables.fontStd,
    fontSize: cssVariables['font-size-sm'],
    fontWeight: 'bold',
  };
}

export function iconLabel(char, color, fontSize, className) {
  return {
    color: color || cssVariables.colorRed,
    fontFamily: "'Font Awesome 5 Free'",
    fontWeight: '700',
    fontSize: fontSize || cssVariables['heading-4-size'],
    text: char,
    className,
  };
}

// ICON / LABEL COMBINATIONS
// https://developers.google.com/maps/documentation/javascript/reference/marker#MarkerOptions
// https://developers.google.com/maps/documentation/javascript/reference/polygon#PolylineOptions

const NO_ICON = { url: ' ', size: mapSize(0, 0) };
const NO_LABEL = { text: ' ' };

export function circleShortLabel(label, color) {
  return {
    icon: circleIcon(color || cssVariables.colorRed),
    label: label && shortLabel(label),
  };
}

export function iconOnlyLabel(char, color, fontSize, className) {
  return {
    icon: NO_ICON,
    label: iconLabel(char, color, fontSize, className),
  };
}

export function imgOnlyLabel(url, size) {
  return {
    icon: imgIcon(url, size),
    label: NO_LABEL,
  };
}

export function planeSymbol(rotation) {
  return {
    icon: {
      path: PLANE_PATH,
      scale: 0.06,
      fillColor: cssVariables.colorRed,
      fillOpacity: 1,
      strokeColor: cssVariables.colorRed,
      strokeWeight: 1,
      rotation,
      anchor: point(350, 350),
    },
    label: NO_LABEL,
  };
}

export function planeRoute(color) {
  return {
    options: {
      strokeWeight: 0,
      icons: [{ icon: dashedLineSymbol(color), offset: 0, repeat: '10px' }],
      geodesic: true,
    },
  };
}

// RENDERERS

function ExtendedMarker({ marker }) {
  const [element, setElement] = useState();
  const clusterer = useContext(GoogleMapClustererContext);

  const listenerProps = useGoogleMapTooltip({
    getContent: () => marker.tooltipContent,
    onMouseOver: marker.onMouseOver,
    onMouseOut: marker.onMouseOut,
    getElement: () => element,
  });

  return (
    <Marker
      clusterer={clusterer}
      position={marker.coords}
      icon={marker.icon}
      shape={marker.shape}
      label={marker.label}
      title={marker.title}
      options={{ data: marker.data }}
      onLoad={setElement}
      {...listenerProps}
    />
  );
}
export function renderMarker(marker, index) {
  return <ExtendedMarker key={`${marker.key}${index}`} marker={marker} />;
}

export function renderPolyline(polyline) {
  return (
    <Polyline
      key={polyline.key}
      path={polyline.path}
      options={polyline.options}
    />
  );
}
